import { Controller } from "@hotwired/stimulus"
import { getCsrfToken } from "../utils/csrf.util"
import { isAbortError, rethrowUnlessAbort } from "../utils/fetch-error.util"
import { tableClientToken } from "../utils/table_client_token"
import TableFrameNavigator from "../utils/table_frame_navigator"
import TableQueryState from "../utils/table_query_state"

const INLINE_EDIT_CONFIRMATION_STORAGE_KEY = "inline-edit-confirmation"
const INLINE_EDIT_CONFIRMATION_DURATION = 3000
const INLINE_EDIT_CONFIRMATION_BADGE_CLASS = "lc-inline-edit-confirmation-badge"

export default class extends Controller {
  static targets = ["panel", "input"]

  static values = {
    url: String,
    field: String,
    confirmationKey: String
  }

  connect() {
    this.saveRevision = 0
    this.clientToken = tableClientToken()
    const frameId = this.element.closest("turbo-frame[id]")?.id || "documents"
    const tableKey = this.element.closest(".documents-table-controller")?.dataset.tableNavigationTableKeyValue || null
    this.tableNavigator = new TableFrameNavigator({ frameId, tableKey })
    this.boundHandleDocumentClick = this.#handleDocumentClick.bind(this)
    document.addEventListener("click", this.boundHandleDocumentClick, true)
    this.#consumeConfirmation()
  }

  disconnect() {
    document.removeEventListener("click", this.boundHandleDocumentClick, true)
    window.clearTimeout(this.confirmationTimeout)
    this.#cancelConfirmationReplay()
    this.confirmationBadge?.remove()
    this.saveAbortController?.abort()
    this.tableNavigator?.disconnect()
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()

    const shouldOpen = this.panelTarget.hidden
    if (shouldOpen) this.#syncInputFromBadge()
    this.panelTarget.hidden = !shouldOpen
    this.element.classList.toggle("is-open", shouldOpen)
  }

  // The status badge can be replaced by external broadcasts (e.g. a paired
  // bank transaction flips the invoice to "paid"). Re-sync the dropdown
  // from the badge's `data-document-status` so the user doesn't unknowingly
  // overwrite the live status with the stale page-load value.
  #syncInputFromBadge() {
    if (!this.hasInputTarget) return
    const badge = this.element.querySelector("[data-document-status]")
    if (!badge) return
    const currentValue = badge.dataset.documentStatus
    if (!currentValue) return
    if (this.inputTarget.value !== currentValue) this.inputTarget.value = currentValue
  }

  cancel(event) {
    event.preventDefault()
    event.stopPropagation()
    this.#close()
  }

  triggerConfirmation() {
    this.element.classList.remove("is-confirmed")
    void this.element.offsetWidth
    this.#showConfirmation()
  }

  async save(event) {
    event.preventDefault()
    event.stopPropagation()

    const revision = ++this.saveRevision
    this.saveAbortController?.abort()
    this.saveAbortController = new AbortController()
    try {
      const response = await fetch(this.urlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
          "X-Table-Client-Token": this.clientToken
        },
        signal: this.saveAbortController.signal,
        body: JSON.stringify({
          field: this.fieldValue,
          value: this.inputTarget.value
        })
      })

      const responseData = await this.#parseJsonResponse(response)
      if (revision !== this.saveRevision) return
      if (!response.ok) {
        this.#showToast(responseData.toast, { fallbackMessage: responseData.error })
        return
      }

      if (responseData.confirmation_key) {
        sessionStorage.setItem(INLINE_EDIT_CONFIRMATION_STORAGE_KEY, responseData.confirmation_key)
      }

      this.#close()

      if (this.#requiresTableRefresh(responseData.reconciliation)) {
        this.tableNavigator.navigate(new TableQueryState(window.location.search), {
          history: "none",
          reason: "inline-edit",
          preserveTableScroll: true,
          restoreFocusSelector: this.#focusRestoreSelector(responseData.confirmation_key)
        })
        this.#replayConfirmation(responseData.confirmation_key, responseData.cell_id)
      } else if (responseData.html && responseData.cell_id) {
        this.#renderCellUpdate(responseData.html, responseData.cell_id)
        this.#replayConfirmation(responseData.confirmation_key, responseData.cell_id)
      } else if (responseData.confirmation_key) {
        this.#showConfirmation()
      }
    } catch (error) {
      // A superseded save or an abort leaves the UI alone — the user moved on.
      // Anything else is a real failure: surface it AND re-raise so the global
      // handler reports it (frontend/stimulus-controllers.md §1).
      if (revision !== this.saveRevision || isAbortError(error)) return
      this.#showToast(null, { fallbackMessage: this.element.dataset.inlineEditNetworkErrorMessage })
      rethrowUnlessAbort(error)
    }
  }

  #handleDocumentClick(event) {
    if (this.panelTarget.hidden) return
    if (this.element.contains(event.target)) return
    this.#close()
  }

  #close() {
    this.panelTarget.hidden = true
    this.element.classList.remove("is-open")
  }

  async #parseJsonResponse(response) {
    const contentType = response.headers.get("content-type") || ""
    if (!contentType.includes("application/json")) return {}

    try {
      return await response.json()
    } catch (_error) {
      return {}
    }
  }

  #consumeConfirmation() {
    const pendingKey = sessionStorage.getItem(INLINE_EDIT_CONFIRMATION_STORAGE_KEY)
    if (!pendingKey) return
    if (!(this.hasConfirmationKeyValue && pendingKey === this.confirmationKeyValue)) return

    sessionStorage.removeItem(INLINE_EDIT_CONFIRMATION_STORAGE_KEY)
    this.#showConfirmation()
  }

  #showConfirmation() {
    this.#showFloatingConfirmationBadge()
    this.element.classList.add("is-confirmed")
    window.clearTimeout(this.confirmationTimeout)
    this.confirmationTimeout = window.setTimeout(() => {
      this.element.classList.remove("is-confirmed")
      this.confirmationBadge?.remove()
      this.confirmationBadge = null
    }, INLINE_EDIT_CONFIRMATION_DURATION)
  }

  // Retries for up to 12 × (one frame + 40ms) waiting for the target cell to
  // appear. Both handles are tracked so disconnect() can stop the loop: a
  // controller that has gone away must not keep polling for half a second, and
  // in tests that pending timer outlives the jsdom environment and blows up on
  // a global that no longer exists.
  #replayConfirmation(confirmationKey, cellId, attemptsRemaining = 12) {
    if (!(confirmationKey || cellId) || attemptsRemaining <= 0) return

    this.confirmationReplayFrame = requestAnimationFrame(() => {
      this.confirmationReplayFrame = null
      const confirmationTarget = this.#findConfirmationTarget(confirmationKey, cellId)

      if (confirmationTarget) {
        sessionStorage.removeItem(INLINE_EDIT_CONFIRMATION_STORAGE_KEY)
        const controller = this.application.getControllerForElementAndIdentifier(confirmationTarget, "inline-edit")
        if (controller) {
          controller.triggerConfirmation()
          return
        }
      }

      this.confirmationReplayTimeout = window.setTimeout(() => {
        this.confirmationReplayTimeout = null
        this.#replayConfirmation(confirmationKey, cellId, attemptsRemaining - 1)
      }, 40)
    })
  }

  #cancelConfirmationReplay() {
    if (this.confirmationReplayFrame != null) {
      cancelAnimationFrame(this.confirmationReplayFrame)
      this.confirmationReplayFrame = null
    }
    window.clearTimeout(this.confirmationReplayTimeout)
    this.confirmationReplayTimeout = null
  }

  #showFloatingConfirmationBadge() {
    this.confirmationBadge?.remove()

    const badge = document.createElement("span")
    badge.className = INLINE_EDIT_CONFIRMATION_BADGE_CLASS
    badge.innerHTML = '<span class="material-symbols-outlined" aria-hidden="true">check</span>'
    document.body.appendChild(badge)

    const host = this.element.closest("td, th") || this.element
    const rect = host.getBoundingClientRect()
    const top = Math.max(16, rect.top + rect.height / 2)
    const left = Math.min(window.innerWidth - 16, rect.right - 6)

    badge.style.top = `${top}px`
    badge.style.left = `${left}px`

    requestAnimationFrame(() => {
      badge.classList.add("is-visible")
    })

    this.confirmationBadge = badge
  }

  #findConfirmationTarget(confirmationKey, cellId) {
    const cell = cellId ? document.getElementById(cellId) : null
    if (cell) {
      const controllerElement = cell.querySelector("[data-controller~='inline-edit']")
      if (controllerElement) return controllerElement
    }

    return Array.from(document.querySelectorAll("[data-inline-edit-confirmation-key-value]")).find(
      (element) => element.dataset.inlineEditConfirmationKeyValue === confirmationKey
    )
  }

  #renderCellUpdate(html, cellId) {
    const target = cellId ? document.getElementById(cellId) : null
    if (!(target && html)) return

    target.outerHTML = html
    const fragment = document.getElementById(cellId)
    document.dispatchEvent(
      new CustomEvent("lucanto:table-fragment-replaced", {
        detail: { fragment, kind: "cell", cellId }
      })
    )
  }

  #requiresTableRefresh(reconciliation) {
    if (!reconciliation || reconciliation.intent === "cell") return false
    if (reconciliation.intent === "refresh") return true
    if (reconciliation.intent !== "refresh_if_active") return false

    const params = new URLSearchParams(window.location.search)
    const hasActiveQuery = (reconciliation.query_keys || []).some((key) => params.has(key))
    const effectiveSort = params.get("sort") || reconciliation.default_sort_column
    const hasActiveSort = (reconciliation.sort_columns || []).includes(effectiveSort)
    return hasActiveQuery || hasActiveSort
  }

  #focusRestoreSelector(confirmationKey) {
    if (!confirmationKey) return null
    return `[data-inline-edit-confirmation-key-value=${JSON.stringify(confirmationKey)}] .lc-inline-edit__trigger`
  }

  #showToast(toast, { fallbackMessage } = {}) {
    const container = document.getElementById("toast_container")
    if (!container) return

    const html = toast?.html || toast?.toast_html
    if (html) {
      const template = document.createElement("template")
      template.innerHTML = html.trim()
      container.appendChild(template.content)
      return
    }

    // Server returned an error (or fetch rejected) but didn't ship toast HTML
    // — e.g. CanCan::AccessDenied / RecordNotFound rescues, or offline.
    // Render a minimal, escaped fallback so the user gets feedback.
    const message = toast?.message || fallbackMessage
    if (!message) return

    const fallback = document.createElement("div")
    fallback.className = "toast lc-toast lc-toast--error"
    fallback.setAttribute("role", "alert")
    fallback.textContent = message
    container.appendChild(fallback)
  }
}
